// Single place for all homepage copy — edit text here, not in components.

// Change this (or set NEXT_PUBLIC_SITE_URL) when the custom domain is live — every URL, sitemap and schema uses it.
export const SITE_URL = (process.env.NEXT_PUBLIC_SITE_URL ?? "https://subhan-dev-portfolio.vercel.app").replace(/\/$/, "");
export const PERSON_NAME = "Subhan Nadeem";
export const PERSON_ID = `${SITE_URL}/#person`;

export const contact = {
  whatsapp: "https://wa.me/923057474003",
  email: "subhannadeem1313@gmail.com",
  github: "https://github.com/subhannadeem1",
  linkedin: "https://www.linkedin.com/in/subhan-nadeem-dev/",
  instagram: "https://www.instagram.com/ch_subhan13/",
};

export const hero = {
  eyebrow: "Available for new projects",
  titleStart: "I build software & automations that",
  titleHighlight: "run your business.",
  subtitle:
    "Senior full-stack engineer with 4+ years of shipping production systems: configurators, management platforms, AI agents and end-to-end workflow automation for companies in energy, solar, fintech and SaaS.",
  stats: [
    { value: "4+", label: "Years building production software" },
    { value: "20+", label: "Products shipped for clients" },
    { value: "E2E", label: "From idea and design to deploy" },
  ],
};

export type StackGroup = { title: string; items: string[] };

export const stack: StackGroup[] = [
  { title: "Languages", items: ["TypeScript", "JavaScript", "Python", "SQL"] },
  { title: "Frontend", items: ["React", "Next.js", "Tailwind CSS", "Redux Toolkit", "shadcn/ui"] },
  { title: "Backend & APIs", items: ["Node.js", "REST APIs", "Webhooks", "Stripe"] },
  { title: "Data", items: ["PostgreSQL", "Supabase", "MongoDB", "Vector DBs"] },
  { title: "AI & Automation", items: ["OpenAI API", "LangChain", "Vercel AI SDK", "AI Agents"] },
  { title: "Cloud & DevOps", items: ["AWS", "Vercel", "Docker", "GitHub"] },
];

export type Client = {
  name: string;
  industry: string;
  work: string;
  projectId: string; // matches constants/projects.ts id, opens the project modal
};

export const clients: Client[] = [
  {
    name: "Ralux Solar",
    industry: "Solar mounting systems",
    work: "Configurator with structural load engine, map-based layouts, auto BOM and admin panel.",
    projectId: "Ralux Solar",
  },
  {
    name: "NatPower Energy",
    industry: "Energy · UK",
    work: "Animated energy-park platform with Mapbox and fully dynamic content.",
    projectId: "NatPower",
  },
  {
    name: "OptionsSight",
    industry: "Fintech · Trading",
    work: "Options-flow dashboard, drag-and-drop widgets and a token-based design system.",
    projectId: "OptionsSight",
  },
  {
    name: "Flowtopia",
    industry: "Fintech · Trading",
    work: "Options analytics platform with live data, notifications and an AI trading bot.",
    projectId: "Flowtopia",
  },
];

// Smaller names for the scrolling strip under the client cards
export const moreClients = [
  "The Hockey Review",
  "Alpha Padel",
  "Pod Transcripts",
  "Sora AI",
  "GemHunters",
  "Mocha Coin",
];

export type Service = {
  key: "automation" | "configurator" | "management" | "ai" | "saas" | "dashboards";
  title: string;
  description: string;
  points: string[];
  href?: string; // dedicated service page, when there is one
};

export const services: Service[] = [
  {
    key: "automation",
    href: "/services/business-automation",
    title: "Business Process Automation",
    description:
      "I map how work moves through your company, then replace the manual steps with systems that run on their own.",
    points: ["Quotes, approvals & data entry", "Tool & API integrations", "Auto emails, reports & alerts"],
  },
  {
    key: "configurator",
    href: "/services/solar-racking-configurator",
    title: "Configurators & Quoting Engines",
    description:
      "Product configurators with a real calculation engine behind them: rules, engineering formulas, pricing and BOM.",
    points: ["Rule & formula engines", "Instant BOM & pricing", "Map / 2D / 3D design tools"],
  },
  {
    key: "management",
    href: "/services/management-systems",
    title: "Management Systems",
    description:
      "Custom CRMs, order, inventory and project systems with admin panels, built around your workflow.",
    points: ["Role-based admin panels", "Orders, stock & customers", "One source of truth"],
  },
  {
    key: "ai",
    href: "/services/ai-agents-chatbots",
    title: "AI Agents & Chatbots",
    description:
      "Assistants trained on your own documents and data that answer customers, qualify leads and do the busywork.",
    points: ["RAG on your documents", "Lead-qualifying bots", "AI features in your product"],
  },
  {
    key: "saas",
    title: "SaaS & Web Applications",
    description:
      "Full-stack products from authentication and payments to multi-user dashboards, shipped to production.",
    points: ["Auth, roles & billing", "Scalable Next.js + Node", "Pixel-perfect from Figma"],
  },
  {
    key: "dashboards",
    title: "Dashboards & Data Visualization",
    description:
      "Real-time dashboards, analytics, charts and map-based interfaces that turn raw data into decisions.",
    points: ["Live charts & heatmaps", "Maps & geo data", "KPI & reporting views"],
  },
];

export const industryFocus = {
  eyebrow: "Industry focus",
  title: "Solar & racking companies",
  subtitle: "Your whole sales-to-install flow, automated.",
  description:
    "I've built mounting-system configurators with country-specific structural load engines covering 20+ systems, map-based panel layouts and instant BOM generation. Now I help installers and racking manufacturers automate everything around it.",
  image: "/projects/ralux-solar.png",
  flow: ["Site on map", "Panel layout", "Structural calc", "BOM & pricing", "Quote", "Orders & admin"],
  note: "Not in solar? Same approach works for any team still running on spreadsheets.",
};

export const processSteps = [
  {
    step: "01",
    title: "Discover",
    description: "We walk through your current workflow, tools and bottlenecks, including every manual step.",
  },
  {
    step: "02",
    title: "Blueprint",
    description: "I map the automated flow, data model and scope, split into clear milestones.",
  },
  {
    step: "03",
    title: "Build",
    description: "Weekly demos you can click through, integrated with the tools you already use.",
  },
  {
    step: "04",
    title: "Launch & support",
    description: "Deploy, onboard your team and keep improving as the business grows.",
  },
];

export const cta = {
  title: "Still running on spreadsheets, emails and copy-paste?",
  subtitle: "Tell me how your team works today and I'll show you what can be automated.",
};

// ---------------------------------------------------------------- About page

export const about = {
  eyebrow: "About me",
  title: "I turn messy workflows into software that just works.",
  paragraphs: [
    "I'm Subhan, a senior full-stack engineer with 4+ years of building production software with Next.js, React, Node.js and Python. Most of my work sits where business logic gets complicated: product configurators with real calculation engines, management systems that replace spreadsheets, and AI agents that take repetitive work off a team's plate.",
    "I've built for companies in energy, solar, fintech and SaaS, from a UK energy developer's web platform to a solar mounting configurator that calculates structural loads across 20+ systems. Right now I'm helping solar and racking companies automate their full sales-to-install flow, while still taking on automation projects in other industries.",
  ],
  facts: [
    { label: "Experience", value: "4+ years" },
    { label: "Based in", value: "Pakistan · GMT+5" },
    { label: "Works", value: "Remote, worldwide" },
    { label: "Focus", value: "Automation & configurators" },
  ],
};

export type Engagement = {
  company: string;
  industry: string;
  role: string;
  highlights: string[];
  stack: string[];
  projectId: string;
};

export const engagements: Engagement[] = [
  {
    company: "Ralux Solar",
    industry: "Solar mounting systems",
    role: "Full-stack engineer",
    highlights: [
      "Node.js calculation engine for country-specific structural load formulas across 20+ mounting systems",
      "Map drawing tools to select the install area and place panels directly on the map",
      "Automatic 2D mounting layouts and instant Bill of Materials with cost breakdown",
      "Admin panel to manage systems, variants and project data",
    ],
    stack: ["Next.js", "Node.js", "Supabase"],
    projectId: "Ralux Solar",
  },
  {
    company: "NatPower Energy",
    industry: "Energy · UK",
    role: "Frontend engineer",
    highlights: [
      "Fully animated web platform for a UK energy company",
      "Four energy parks with six dynamic pages each, all driven by data",
      "Interactive Mapbox maps for site locations",
    ],
    stack: ["Next.js", "Mapbox", "Tailwind CSS"],
    projectId: "NatPower",
  },
  {
    company: "OptionsSight",
    industry: "Fintech · Trading",
    role: "Frontend engineer",
    highlights: [
      "Pixel-accurate Next.js build of the Figma design, used as the design-system source of truth",
      "Heatmaps, dealer-positioning charts and live flow tables",
      "Drag-and-drop widget dashboard, auth, onboarding and billing flows",
      "Token-based dark/light design system across five breakpoints",
    ],
    stack: ["Next.js", "TypeScript", "Recharts"],
    projectId: "OptionsSight",
  },
  {
    company: "Flowtopia",
    industry: "Fintech · Trading",
    role: "Frontend & API integration",
    highlights: [
      "Ticker dashboard with live data flows and API integrations",
      "Notifications, training module and an AI trading-assistant bot",
    ],
    stack: ["Next.js", "Redux Toolkit", "OpenAI"],
    projectId: "Flowtopia",
  },
];

export const strengths = [
  {
    key: "systems",
    title: "Systems thinking",
    description: "I start from your workflow and data, then design the software around it, not the other way round.",
  },
  {
    key: "ownership",
    title: "End-to-end ownership",
    description: "Frontend, backend, database and deployment, with one person accountable for all of it.",
  },
  {
    key: "logic",
    title: "Engineering-grade logic",
    description: "Calculation engines, rules and formulas that have to be right, not just look right.",
  },
  {
    key: "communication",
    title: "Clear communication",
    description: "Milestones, weekly demos and plain-English updates. You always know where things stand.",
  },
] as const;
